import React, { Component } from "react";
import API from "../utils/api";
import { toast } from "react-toastify";
import Select from "react-select";

class IncentivesPage extends Component {
  state = {
    plans: [],
    teams: [],
    loading: true,
    showCreateForm: false,
    
    // Form state
    editingPlanId: null,
    newPlanName: "",
    newPlanType: "daily",
    newPlanTeam: null,
    newPlanStart: "",
    newPlanEnd: "",
    newPlanFlaggedLeadLimit: "",
    slabs: [{ metric_type: "posted", min_count: 0, max_count: "", points_per_lead: 0 }],
  };

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    this.setState({ loading: true });
    try {
      const [plansRes, teamsRes] = await Promise.all([
        API.get("/incentives"),
        API.get("/teams")
      ]);
      
      this.setState({
        plans: plansRes.data.data || [],
        teams: teamsRes.data.data || [],
        loading: false
      });
    } catch (e) {
      toast.error("Failed to load data");
      this.setState({ loading: false });
    }
  };

  handleAddSlab = () => {
    this.setState(prevState => ({
      slabs: [...prevState.slabs, { metric_type: "posted", min_count: 0, max_count: "", points_per_lead: 0 }]
    }));
  };

  handleRemoveSlab = (index) => {
    this.setState(prevState => ({
      slabs: prevState.slabs.filter((s, i) => i !== index)
    }));
  };

  handleSlabChange = (index, field, value) => {
    this.setState(prevState => {
      const newSlabs = [...prevState.slabs];
      newSlabs[index][field] = value;
      return { slabs: newSlabs };
    });
  };

  handleEditPlan = (plan) => {
    this.setState({
      showCreateForm: true,
      editingPlanId: plan.id,
      newPlanName: plan.name,
      newPlanType: plan.plan_type || "daily",
      newPlanTeam: plan.team_id ? { label: plan.team_name, value: plan.team_id } : null,
      newPlanStart: plan.effective_from.split('T')[0],
      newPlanEnd: plan.effective_to ? plan.effective_to.split('T')[0] : "",
      newPlanFlaggedLeadLimit: plan.flagged_lead_limit === null ? "" : plan.flagged_lead_limit,
      slabs: plan.slabs.map(s => ({ ...s, max_count: s.max_count === null ? "" : s.max_count }))
    });
  };

  handleCreatePlan = async (e) => {
    e.preventDefault();
    const { newPlanName, newPlanType, newPlanTeam, newPlanStart, newPlanEnd, newPlanFlaggedLeadLimit, slabs, editingPlanId } = this.state;
    if (!newPlanName || !newPlanStart || slabs.length === 0) {
      return toast.error("Please fill all required fields and add at least one slab");
    }

    try {
      const endpoint = editingPlanId ? "/incentive/update" : "/incentive";
      const payload = { 
        name: newPlanName, 
        plan_type: newPlanType,
        team_id: newPlanTeam ? newPlanTeam.value : null,
        effective_from: newPlanStart,
        effective_to: newPlanEnd || null,
        flagged_lead_limit: newPlanFlaggedLeadLimit === "" ? null : Number(newPlanFlaggedLeadLimit),
        slabs: slabs.map(s => ({
          ...s,
          max_count: s.max_count === "" ? null : Number(s.max_count)
        }))
      };
      if (editingPlanId) payload.id = editingPlanId;

      await API.post(endpoint, payload);
      toast.success(editingPlanId ? "Incentive Plan updated" : "Incentive Plan created");
      this.setState({ 
        showCreateForm: false, 
        editingPlanId: null,
        newPlanName: "", 
        newPlanType: "daily",
        newPlanTeam: null,
        newPlanStart: "",
        newPlanEnd: "",
        newPlanFlaggedLeadLimit: "",
        slabs: [{ metric_type: "posted", min_count: 0, max_count: "", points_per_lead: 0 }]
      });
      this.fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to save plan");
    }
  };

  handleStopPlan = async (id) => {
    if (!window.confirm("Are you sure you want to stop this plan?")) return;
    try {
      await API.post("/incentive/stop", { id });
      toast.success("Plan stopped");
      this.fetchData();
    } catch (e) {
      toast.error("Failed to stop plan");
    }
  };

  handleDeletePlan = async (id) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    try {
      await API.post("/incentive/delete", { id });
      toast.success("Plan deleted");
      this.fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to delete plan");
    }
  };

  render() {
    const { plans, teams, loading, showCreateForm, newPlanName, newPlanType, newPlanTeam, newPlanStart, newPlanEnd, newPlanFlaggedLeadLimit, slabs } = this.state;

    const teamOptions = [{ label: "Global (All Teams)", value: null }, ...teams.map(t => ({ label: t.name, value: t.id }))];

    return (
      <div className="container-fluid p-4" style={{ backgroundColor: "#ecf0f5", minHeight: "100vh" }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3>Incentive Plans</h3>
          <button className="btn btn-primary" onClick={() => this.setState({ showCreateForm: !showCreateForm })}>
            {showCreateForm ? "Cancel" : "Create Plan"}
          </button>
        </div>

        {showCreateForm && (
          <div className="card mb-4">
            <div className="card-body">
              <form onSubmit={this.handleCreatePlan}>
                <div className="row g-3 mb-3">
                  <div className="col-md-3">
                    <label>Plan Name</label>
                    <input type="text" className="form-control" value={newPlanName} onChange={e => this.setState({ newPlanName: e.target.value })} required />
                  </div>
                  <div className="col-md-2">
                    <label>Plan Type</label>
                    <select className="form-control" value={newPlanType} onChange={e => this.setState({ newPlanType: e.target.value })}>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label>Target Team</label>
                    <Select
                      options={teamOptions}
                      value={newPlanTeam}
                      onChange={val => this.setState({ newPlanTeam: val })}
                      placeholder="Global (All Teams)"
                    />
                  </div>
                  <div className="col-md-2">
                    <label>Effective From</label>
                    <input type="date" className="form-control" value={newPlanStart} onChange={e => this.setState({ newPlanStart: e.target.value })} required />
                  </div>
                  <div className="col-md-2">
                    <label>Effective To</label>
                    <input type="date" className="form-control" value={newPlanEnd} onChange={e => this.setState({ newPlanEnd: e.target.value })} />
                  </div>
                  <div className="col-md-3">
                    <label>Flagged Limit (Optional)</label>
                    <input type="number" className="form-control" value={newPlanFlaggedLeadLimit} onChange={e => this.setState({ newPlanFlaggedLeadLimit: e.target.value })} min="0" placeholder="No limit" />
                  </div>
                </div>

                <h6 className="mt-4 mb-2">Incentive Slabs</h6>
                {slabs.map((slab, i) => (
                  <div className="row g-2 mb-2 align-items-end" key={i}>
                    <div className="col-md-2">
                      <label>Metric</label>
                      <select className="form-control" value={slab.metric_type} onChange={e => this.handleSlabChange(i, "metric_type", e.target.value)}>
                        <option value="posted">Posted Leads</option>
                        <option value="attempted">Attempted Leads</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label>Min Count</label>
                      <input type="number" className="form-control" value={slab.min_count} onChange={e => this.handleSlabChange(i, "min_count", Number(e.target.value))} required />
                    </div>
                    <div className="col-md-2">
                      <label>Max Count</label>
                      <input type="number" className="form-control" value={slab.max_count} onChange={e => this.handleSlabChange(i, "max_count", e.target.value)} placeholder="Infinity" />
                    </div>
                    <div className="col-md-2">
                      <label>Points/Lead</label>
                      <input type="number" className="form-control" value={slab.points_per_lead} onChange={e => this.handleSlabChange(i, "points_per_lead", Number(e.target.value))} required />
                    </div>
                    <div className="col-md-2">
                      <button type="button" className="btn btn-danger btn-sm mb-1" onClick={() => this.handleRemoveSlab(i)}>Remove</button>
                    </div>
                  </div>
                ))}
                
                <button type="button" className="btn btn-secondary btn-sm mb-3" onClick={this.handleAddSlab}>+ Add Slab</button>
                <br />
                <button type="submit" className="btn btn-success">Save Plan</button>
              </form>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-body p-0">
            {loading ? (
              <div className="p-4 text-center">Loading...</div>
            ) : (
              <table className="table table-striped table-hover mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Team</th>
                    <th>Effective Period</th>
                    <th>Flagged Limit</th>
                    <th>Status</th>
                    <th>Slabs</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map(plan => (
                    <tr key={plan.id}>
                      <td>{plan.name}</td>
                      <td><span className="badge bg-secondary">{plan.plan_type ? plan.plan_type.toUpperCase() : 'DAILY'}</span></td>
                      <td>{plan.team_name || "Global"}</td>
                      <td>{new Date(plan.effective_from).toLocaleDateString()} - {plan.effective_to ? new Date(plan.effective_to).toLocaleDateString() : 'Ongoing'}</td>
                      <td>{plan.flagged_lead_limit !== null ? plan.flagged_lead_limit : 'No limit'}</td>
                      <td>
                        <span className={`badge ${
                          plan.dynamic_status === 'active' ? 'bg-success' : 
                          plan.dynamic_status === 'upcoming' ? 'bg-info' :
                          plan.dynamic_status === 'expired' ? 'bg-warning' :
                          'bg-danger'
                        }`}>
                          {plan.dynamic_status.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <ul className="mb-0 pl-3">
                          {plan.slabs?.map(s => (
                            <li key={s.id}>{s.metric_type}: {s.min_count} to {s.max_count || '∞'} = {s.points_per_lead} pts</li>
                          ))}
                        </ul>
                      </td>
                      <td>
                        {plan.dynamic_status === 'upcoming' && (
                          <>
                            <button className="btn btn-sm btn-primary text-white me-2" onClick={() => this.handleEditPlan(plan)}>
                              Edit
                            </button>
                            <button className="btn btn-sm btn-danger text-white" onClick={() => this.handleDeletePlan(plan.id)}>
                              Delete
                            </button>
                          </>
                        )}
                        {plan.dynamic_status === 'active' && (
                          <button className="btn btn-sm btn-danger text-white" onClick={() => this.handleStopPlan(plan.id)}>
                            Stop Plan
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {plans.length === 0 && (
                    <tr><td colSpan="8" className="text-center">No incentive plans found</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default IncentivesPage;
